import { RoleS, srRoles, getApiRole } from "@ugg/shared/utils/role-helpers";
import { StatKey, PlayerStats, RankStats } from "../RoleBreakdownModule";
import { availableTiersAsc } from "@ugg/shared/utils/rank-helpers";

const BASE_URL = "https://stats2.u.gg/year-in-review-performances";

interface RawStatsByTier {
  [tier: string]: number;
}
interface RawJsonData {
  kills: RawStatsByTier;
  assists: RawStatsByTier;
  deaths: RawStatsByTier;
  cs: RawStatsByTier;
  jungle_cs: RawStatsByTier;
  heals: RawStatsByTier;
  damage: RawStatsByTier;
  damage_mitigated: RawStatsByTier;
  match_duration: RawStatsByTier; // In seconds
  wards_placed: RawStatsByTier;
  gold: RawStatsByTier;
  count: RawStatsByTier;
}

// --- RankStats Transformer ---

export function transformJsonToRankStats(rawData: RawJsonData, targetRole: RoleS): RankStats {
  const rankStats: RankStats = Array.from({ length: availableTiersAsc.length }, () => ({}));

  availableTiersAsc.forEach((tier, index) => {
    const baseStats = {
      kills: rawData.kills[tier] ?? 0,
      assists: rawData.assists[tier] ?? 0,
      deaths: rawData.deaths[tier] ?? 1,
      cs: rawData.cs[tier] ?? 0,
      jungle_cs: rawData.jungle_cs[tier] ?? 0,
      gold: rawData.gold[tier] ?? 0,
      wards_placed: rawData.wards_placed[tier] ?? 0,
      dmg_mitigated: rawData.damage_mitigated[tier] ?? 0,
      healing: rawData.heals[tier] ?? 0,
      damage: rawData.damage[tier] ?? 0,
      match_duration_sec: rawData.match_duration[tier] ?? 1,
    };

    const matchDurationMin = baseStats.match_duration_sec / 60;
    const roleStats: Record<StatKey, number> = {} as Record<StatKey, number>;

    roleStats.kda = (baseStats.kills + baseStats.assists) / baseStats.deaths;

    roleStats.csMin = (baseStats.cs + baseStats.jungle_cs) / matchDurationMin;
    roleStats.goldMin = baseStats.gold / matchDurationMin;
    roleStats.dmgMin = baseStats.damage / matchDurationMin;

    roleStats.wardsGame = baseStats.wards_placed;
    roleStats.dmgMitigated = baseStats.dmg_mitigated;
    roleStats.healing = baseStats.healing;

    rankStats[index][targetRole] = roleStats;
  });

  return rankStats;
}

export async function getRankStatsForAllRoles(): Promise<RankStats | null> {
  const INITIAL_RANK_STATS: RankStats = Array.from({ length: 10 }, () => ({}));

  const rolePromises = srRoles.map(async (role) => {
    const apiRole = getApiRole(role);
    const url = `${BASE_URL}/${apiRole}.json`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${role} data: ${response.statusText}`);
      }
      const rawData: RawJsonData = await response.json();

      return transformJsonToRankStats(rawData, role);
    } catch (error) {
      console.error(`Error fetching or transforming data for role ${role}:`, error);
      return INITIAL_RANK_STATS;
    }
  });

  try {
    const allRoleStats = await Promise.all(rolePromises);
    const finalRankStats: RankStats = INITIAL_RANK_STATS.map((_, tierIndex) => {
      const tierStats: PlayerStats = {};
      allRoleStats.forEach((roleStats) => {
        const statsForTier = roleStats[tierIndex];
        Object.assign(tierStats, statsForTier);
      });

      return tierStats;
    });

    return finalRankStats;
  } catch (error) {
    console.error("An error occurred during bulk data processing:", error);
    return null;
  }
}
